import { useEffect, useState } from "react";
import { AuthProvider, useAuth, AuthError } from "./core/platform/auth/AuthProvider";
import { PinPad } from "./core/ui/components/PinPad";
import { Logo } from "./theme/Logo";
import { Till, type TillConfig } from "./core/ui/till/Till";
import { ShiftOpen } from "./core/ui/till/ShiftOpen";
import { ShiftClose } from "./core/ui/till/ShiftClose";
import { openShift, closeShiftLocally, readXReading } from "./core/data/local/shift";
import { getOpenShift } from "./core/data/local/repositories";
import { money, type Money } from "./core/domain/money/money";
import type { LocalShift } from "./core/data/local/db";
import type { Reading } from "./core/domain/shift/reading";
import { SyncStatusIndicator } from "./core/ui/components/SyncStatus";
import { RecentSales } from "./core/ui/till/RecentSales";
import { CupsCounter } from "./core/ui/till/CupsCounter";
import { MenuManager } from "./core/ui/backoffice/MenuManager";
import { CorrectionDialog } from "./core/ui/till/CorrectionDialog";
import { ManagerAuth } from "./core/ui/components/ManagerAuth";
import { refreshAuthorizers } from "./core/data/local/staff-cache";
import { UpdatePrompt } from "./core/platform/UpdatePrompt";
import { syncScheduler } from "./core/data/sync/scheduler";
import { getMeta, setMeta, META_KEYS, requestPersistentStorage } from "./core/data/local/db";
import { Slot } from "./core/ui/slots/Slot";
import { modules } from "./core/platform/registry/registry";
import { syncEnabledModules } from "./modules";
import { ModuleHost } from "./modules/ModuleHost";
import { brand } from "./theme/brand.config";

/**
 * Application shell.
 *
 * Slice A in progress: sign-in works end to end; the till itself is the next
 * phase (T055 onward). This renders the sign-in surface and, once signed in, a
 * placeholder that names what is built and what is not — deliberately explicit
 * rather than a fake dashboard.
 */

interface StaffOption {
  id: string;
  name: string;
  role: string;
}

function SignIn() {
  const { signIn } = useAuth();
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [selected, setSelected] = useState<StaffOption | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch("/api/staff/directory");
        if (response.ok) setStaff((await response.json()) as StaffOption[]);
      } catch {
        // Offline with no cached directory: the terminal has not completed
        // first-time setup, which requires a connection by design.
      }
    })();
  }, []);

  const submit = async (pin: string) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(selected.id, pin);
    } catch (caught) {
      setError(caught instanceof AuthError ? caught.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <Logo variant="lockup" size={40} />

      {!selected ? (
        <section className="signin__staff">
          <p className="signin__prompt">Who is on the till?</p>
          {staff.length === 0 ? (
            <p className="signin__empty">
              No staff available. Run <code>npm run db:seed:local</code> for development data.
            </p>
          ) : (
            <ul className="signin__list">
              {staff.map((person) => (
                <li key={person.id}>
                  <button type="button" className="signin__person" onClick={() => setSelected(person)}>
                    <span className="signin__name">{person.name}</span>
                    <span className="signin__role">{person.role}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <PinPad
          label={selected.name}
          error={error}
          busy={busy}
          onSubmit={submit}
          onCancel={() => {
            setSelected(null);
            setError(null);
          }}
        />
      )}
    </main>
  );
}

interface Config {
  taxRegime: "vat" | "non_vat";
  taxRateBp: number;
  pricesTaxInclusive: boolean;
  /**
   * Basis points, e.g. 2000 = 20% (RA 9994 / RA 10754). Optional so a config
   * cached before this field existed still boots — see the fallback where
   * `tillConfig` is built.
   */
  statutoryDiscountBp?: number;
  currencyCode: string;
  currencySymbol: string;
  currencyMinorUnits: number;
  timezone: string;
  name: string;
  address: string | null;
  taxIdentifier: string | null;
  receiptHeader: string | null;
  receiptFooter: string | null;
  cashVarianceThreshold: number;
  /** Optional modules this deployment runs, from module_registration. */
  enabledModules?: string[];
}

function Terminal() {
  const { claims, token, signOut, can } = useAuth();
  const [config, setConfig] = useState<Config | null>(null);
  const [terminal, setTerminal] = useState<{ id: string; series: string } | null>(null);
  const [shift, setShift] = useState<LocalShift | null>(null);
  const [closing, setClosing] = useState<Reading | null>(null);
  const [shiftBusy, setShiftBusy] = useState(false);
  const [shiftError, setShiftError] = useState<string | null>(null);
  const [browsing, setBrowsing] = useState(false);
  const [countingCups, setCountingCups] = useState(false);
  const [managingMenu, setManagingMenu] = useState(false);
  /** Bumped when the menu is edited, so the till reloads its grid. */
  const [catalogVersion, setCatalogVersion] = useState(0);
  const [correcting, setCorrecting] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /**
   * Whether an enabled module actually puts something in the settings slot.
   *
   * The registry is not reactive, so this is captured once the enable pass has
   * resolved rather than read during render. A settings button that opens an
   * empty panel is precisely the orphaned interface element SC-012 forbids —
   * with every module off, no button.
   */
  const [hasModuleSettings, setHasModuleSettings] = useState(false);

  /**
   * A manager approval requested by a component that wants a plain answer.
   *
   * `ShiftClose` asks for an approver id and awaits it. Holding the resolver
   * here lets the approval be a real dialog rather than the stub this used to
   * be — which silently made an over-threshold drawer impossible to close.
   */
  const [approvalRequest, setApprovalRequest] = useState<{
    action: string;
    resolve: (managerId: string | null) => void;
  } | null>(null);

  const requestApproval = (action: string) =>
    new Promise<string | null>((resolve) => setApprovalRequest({ action, resolve }));

  /**
   * Replicate the managers who can approve a correction or a variance.
   *
   * Keyed on the token rather than run once in the boot pass, so a session
   * replaced without a remount — signing back in after an expiry, the case that
   * empties this cache in practice — re-replicates too.
   *
   * It is an update and never a dependency: if it fails, whatever was
   * replicated last time still authorises offline, and `ManagerAuth` tries
   * again at the moment of need if it finds nobody.
   */
  useEffect(() => {
    if (!token) return;
    void refreshAuthorizers(token).catch(() => undefined);
  }, [token]);

  useEffect(() => {
    /**
     * LOCAL FIRST, network second.
     *
     * The terminal's own identity and its configuration are cached in
     * IndexedDB, because fetching them on every boot means a reload during an
     * outage renders nothing at all — the exact failure Principle I exists to
     * prevent, and one that would strand a café mid-service with a blank
     * screen.
     *
     * The network refresh that follows is an update, not a dependency.
     */
    void (async () => {
      const cachedConfig = await getMeta<Config>(META_KEYS.businessProfile);
      const cachedTerminal = await getMeta<{ id: string; series: string }>("terminal.identity");

      if (cachedConfig) setConfig(cachedConfig);
      if (cachedTerminal) {
        setTerminal(cachedTerminal);
        await requestPersistentStorage();
        syncScheduler.start();
        setShift((await getOpenShift(cachedTerminal.id)) ?? null);
      }

      const [configResponse, terminalResponse] = await Promise.all([
        fetch("/api/config").catch(() => null),
        fetch("/api/terminal/current").catch(() => null),
      ]);

      if (configResponse?.ok) {
        const fresh = (await configResponse.json()) as Config;
        await setMeta(META_KEYS.businessProfile, fresh);
        setConfig(fresh);
      }

      if (terminalResponse?.ok) {
        const t = (await terminalResponse.json()) as { id: string; series: string };
        await setMeta("terminal.identity", t);
        await setMeta(META_KEYS.terminalId, t.id);
        await setMeta(META_KEYS.series, t.series);
        setTerminal(t);

        if (!cachedTerminal) {
          // Ask the browser to keep our data before any sale is written —
          // without this iOS evicts IndexedDB under storage pressure, which
          // here means losing unsynced sales.
          await requestPersistentStorage();
          syncScheduler.start();
          setShift((await getOpenShift(t.id)) ?? null);
        }
      }
    })();

    return () => syncScheduler.stop();
  }, []);

  /**
   * Load whatever this deployment has switched on in `module_registration`.
   *
   * KEYED ON A JOINED STRING, NOT THE ARRAY. `config` is set twice on a normal
   * boot — once from the IndexedDB cache, once from the network — and a fresh
   * array reference each time would re-run the enable pass on every load,
   * re-probing USB and reopening the device for no reason.
   */
  const moduleKey = config === null ? null : (config.enabledModules ?? []).join(",");

  useEffect(() => {
    if (moduleKey === null) return;
    let cancelled = false;

    void syncEnabledModules(moduleKey === "" ? [] : moduleKey.split(",")).then(() => {
      if (!cancelled) setHasModuleSettings(modules.slotComponents("till.settings").length > 0);
    });

    return () => {
      cancelled = true;
    };
  }, [moduleKey]);

  if (!config || !terminal || !claims) return <div className="boot" />;

  const tillConfig: TillConfig = {
    tax: {
      regime: config.taxRegime,
      rateBp: config.taxRateBp,
      pricesTaxInclusive: config.pricesTaxInclusive,
    },
    // RA 9994's own rate, used only if a stale cached config predates this
    // field — the server's real figure always wins once it has loaded.
    statutoryDiscountBp: config.statutoryDiscountBp ?? 2000,
    currency: {
      currencyCode: config.currencyCode,
      currencySymbol: config.currencySymbol,
      minorUnits: config.currencyMinorUnits,
      locale: brand.locale.locale,
    },
    orderTypes: Object.keys(brand.copy.orderTypes),
    orderTypeLabels: brand.copy.orderTypes,
    tenderTypes: Object.keys(brand.copy.tenderTypes),
    tenderLabels: brand.copy.tenderTypes,
    business: {
      name: config.name,
      address: config.address ?? undefined,
      taxIdentifier: config.taxIdentifier ?? undefined,
      header: config.receiptHeader ?? undefined,
      footer: config.receiptFooter ?? undefined,
    },
    complianceNotice: brand.receipt.complianceNotice,
    timezone: config.timezone,
    // Server-driven, so a cash drawer can be switched on the day it is bought
    // without a deploy. Absent from a cached config written before this
    // existed, which reads as "no modules" — the safe default.
    activeModules: config.enabledModules ?? [],
  };

  // FR-035. The till is not reachable without an open shift — this is a gate,
  // not a prompt, because a sale that lands outside a shift cannot be
  // reconciled against any drawer.
  if (!shift) {
    return (
      <ShiftOpen
        currency={tillConfig.currency}
        staffName={claims.name}
        busy={shiftBusy}
        error={shiftError}
        onOpen={async (openingFloat: Money) => {
          setShiftBusy(true);
          setShiftError(null);
          try {
            setShift(
              await openShift({
                terminalId: terminal.id,
                staffId: claims.sub,
                openingFloat,
                timezone: config.timezone,
              }),
            );
          } catch (caught) {
            setShiftError(caught instanceof Error ? caught.message : "Could not open the shift");
          } finally {
            setShiftBusy(false);
          }
        }}
      />
    );
  }

  return (
    <main className="terminal">
      <header className="terminal__bar">
        <Logo variant="mark" size={22} />
        <span className="terminal__staff">
          {claims.name} · {claims.roleName}
        </span>
        <SyncStatusIndicator />
        <button type="button" className="terminal__signout" onClick={() => setBrowsing(true)}>
          Sales
        </button>
        <button type="button" className="terminal__signout" onClick={() => setCountingCups(true)}>
          Cups
        </button>
        {/* Editing the menu is a manager's job, so a cashier is not shown a
            door they would only be refused at. The server enforces it too. */}
        {can("catalog.write") && (
          <button
            type="button"
            className="terminal__signout"
            onClick={() => setManagingMenu(true)}
          >
            Menu
          </button>
        )}
        {/* Hardware setup is a manager's job, and the button exists only when a
            module actually offers a settings surface — with peripherals off
            there is no door to a room that is not there (SC-012). */}
        {hasModuleSettings && can("drawer.open") && (
          <button
            type="button"
            className="terminal__signout"
            onClick={() => setSettingsOpen(true)}
          >
            Settings
          </button>
        )}
        {/* A module-owned action reachable from anywhere in the till, same
            spirit as Sales/Menu/Settings above — core renders the slot
            without knowing what (if anything) fills it. */}
        <Slot id="terminal.actions" staffId={claims.sub} terminalId={terminal.id} token={token} />
        <button
          type="button"
          className="terminal__signout"
          onClick={async () => setClosing(await readXReading(shift.id))}
        >
          Close till
        </button>
        <button type="button" className="terminal__signout" onClick={signOut}>
          Sign out
        </button>
      </header>

      {closing && (
        <div className="till__overlay">
          <ShiftClose
            reading={closing}
            currency={tillConfig.currency}
            tenderLabels={tillConfig.tenderLabels}
            varianceThreshold={money(config.cashVarianceThreshold ?? 5000)}
            busy={shiftBusy}
            error={shiftError}
            onCancel={() => {
              setClosing(null);
              setShiftError(null);
            }}
            onRequestManager={() => requestApproval("Close the till with a cash variance")}
            onClose={async ({ countedCash, reason, authorizedBy }) => {
              setShiftBusy(true);
              setShiftError(null);
              try {
                await closeShiftLocally({
                  shiftId: shift.id,
                  staffId: claims.sub,
                  countedCash,
                  varianceThreshold: money(config.cashVarianceThreshold ?? 5000),
                  varianceReason: reason,
                  varianceAuthorizedBy: authorizedBy,
                });
                setClosing(null);
                setShift(null);
              } catch (caught) {
                setShiftError(caught instanceof Error ? caught.message : "Could not close");
              } finally {
                setShiftBusy(false);
              }
            }}
          />
        </div>
      )}

      {countingCups && (
        <div className="till__overlay">
          <CupsCounter shiftId={shift.id} onClose={() => setCountingCups(false)} />
        </div>
      )}

      {managingMenu && token && (
        <div className="till__overlay">
          <MenuManager
            token={token}
            currency={tillConfig.currency}
            onClose={() => {
              setManagingMenu(false);
              setCatalogVersion((n) => n + 1);
            }}
          />
        </div>
      )}

      {settingsOpen && (
        <div className="till__overlay">
          <div className="settings">
            <Slot id="till.settings" />
            <div className="settings__actions">
              <button type="button" className="is-primary" onClick={() => setSettingsOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {browsing && (
        <div className="till__overlay">
          <RecentSales
            currency={tillConfig.currency}
            timezone={tillConfig.timezone}
            shiftId={shift.id}
            tenderLabels={tillConfig.tenderLabels}
            onSelect={(saleId) => {
              setBrowsing(false);
              setCorrecting(saleId);
            }}
            onClose={() => setBrowsing(false)}
          />
        </div>
      )}

      {correcting && (
        <div className="till__overlay">
          <CorrectionDialog
            saleId={correcting}
            currency={tillConfig.currency}
            staffId={claims.sub}
            onDone={(message) => {
              setCorrecting(null);
              setNotice(message);
            }}
            onCancel={() => setCorrecting(null)}
          />
        </div>
      )}

      {approvalRequest && (
        <div className="till__overlay">
          <ManagerAuth
            action={approvalRequest.action}
            onAuthorized={(authorization) => {
              approvalRequest.resolve(authorization.managerId);
              setApprovalRequest(null);
            }}
            onCancel={() => {
              approvalRequest.resolve(null);
              setApprovalRequest(null);
            }}
          />
        </div>
      )}

      {notice && (
        <p className="terminal__notice" role="status" onAnimationEnd={() => setNotice(null)}>
          {notice}
        </p>
      )}

      {/* Gives a drawer event its shift, and gives a failed release somewhere to
          ask for the manual acknowledgement. Renders nothing until it must. */}
      <ModuleHost shiftId={shift.id} />

      <Till
        config={tillConfig}
        staffId={claims.sub}
        staffName={claims.name}
        terminalId={terminal.id}
        shiftId={shift.id}
        series={terminal.series}
        onRequestManager={requestApproval}
        catalogVersion={catalogVersion}
      />
    </main>
  );
}

function Root() {
  const { claims, loading } = useAuth();
  if (loading) return <div className="boot" />;
  return claims ? <Terminal /> : <SignIn />;
}

export function App() {
  return (
    <AuthProvider>
      <Root />
      {/* Registered on every load — this is what makes the till work offline.
          An update is offered, never forced: activating a new worker mid-sale
          would discard the cart and could interrupt a tender. */}
      <UpdatePrompt />
    </AuthProvider>
  );
}

export default App;
